import { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Download,
  ExternalLink,
  Mail,
  Linkedin,
  Globe,
  Phone,
  FileText,
  ArrowLeft,
  Trash2,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Field, Select } from '@/components/ui/Field';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import {
  useAdminApplications,
  useAdminRoles,
  useUpdateApplicationStatus,
  useDeleteApplication,
  signedCvUrl,
  APPLICATION_STATUSES,
  type ApplicationRow,
  type ApplicationStatus,
} from '@/lib/admin-queries';
import { can, useAuth } from '@/lib/auth';
import { formatDate } from '@/lib/utils';

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  interview: 'Interview',
  offer: 'Offer',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const STATUS_VARIANTS: Record<ApplicationStatus, 'default' | 'gold' | 'success' | 'warning' | 'danger' | 'outline'> = {
  new: 'gold',
  reviewing: 'outline',
  interview: 'warning',
  offer: 'success',
  rejected: 'danger',
  withdrawn: 'default',
};

export function ApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = searchParams.get('role') ?? 'all';
  const statusFilter = (searchParams.get('status') ?? 'all') as 'all' | ApplicationStatus;

  const { data: apps = [], isLoading } = useAdminApplications(roleFilter);
  const { data: roles = [] } = useAdminRoles();
  const updateStatus = useUpdateApplicationStatus();
  const remove = useDeleteApplication();
  const { role: callerRole } = useAuth();
  const canDelete = can(callerRole ?? null, 'delete_applications');
  const [viewing, setViewing] = useState<ApplicationRow | null>(null);
  const [deleting, setDeleting] = useState<ApplicationRow | null>(null);

  // Keep the URL in sync when the user changes filters via UI.
  function setParam(key: string, value: string | null) {
    if (!value || value === 'all') searchParams.delete(key);
    else searchParams.set(key, value);
    setSearchParams(searchParams, { replace: true });
  }

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return apps;
    return apps.filter((a) => a.status === statusFilter);
  }, [apps, statusFilter]);

  const columns = useMemo(
    () => [
      {
        key: 'applicant',
        header: 'Applicant',
        cell: (a: ApplicationRow) => (
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-subtle text-xs font-medium text-fg-muted">
              {a.full_name
                .split(/\s+/)
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </div>
            <div>
              <p className="font-medium">{a.full_name}</p>
              <p className="text-xs text-fg-subtle">{a.email}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        cell: (a: ApplicationRow) =>
          a.role_slug ? (
            <Badge variant="outline">{a.role_slug}</Badge>
          ) : (
            <Badge>Open application</Badge>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (a: ApplicationRow) => (
          <Badge variant={STATUS_VARIANTS[a.status]}>{STATUS_LABELS[a.status]}</Badge>
        ),
      },
      {
        key: 'cv',
        header: 'CV',
        cell: (a: ApplicationRow) =>
          a.cv_path ? (
            <Badge variant="success">Attached</Badge>
          ) : (
            <span className="text-xs text-fg-subtle">—</span>
          ),
      },
      {
        key: 'received',
        header: 'Received',
        cell: (a: ApplicationRow) => (
          <span className="text-xs text-fg-muted">{formatDate(a.created_at)}</span>
        ),
      },
      ...(canDelete
        ? [
            {
              key: 'actions',
              header: '',
              align: 'right' as const,
              cell: (a: ApplicationRow) => (
                <div className="flex items-center justify-end">
                  <button
                    type="button"
                    aria-label="Delete application"
                    title="Delete application"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleting(a);
                    }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ),
            },
          ]
        : []),
    ],
    [canDelete],
  );

  async function changeStatus(id: string, status: ApplicationStatus) {
    try {
      await updateStatus.mutateAsync({ id, status });
      toast.success(`Marked ${STATUS_LABELS[status]}`);
      setViewing((prev) => (prev && prev.id === id ? { ...prev, status } : prev));
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      const result = await remove.mutateAsync({ id: deleting.id, cv_path: deleting.cv_path });
      // Close both the dialog and the detail sheet — the row is gone.
      setDeleting(null);
      if (viewing?.id === deleting.id) setViewing(null);
      toast.success(
        result?.cv_removed === false && deleting.cv_path
          ? 'Application deleted. CV could not be removed from storage.'
          : 'Application deleted.',
      );
    } catch (err) {
      // Leave `deleting` populated so the admin can retry or cancel.
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Careers</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Applications</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Review and triage incoming applications. CVs live in the private <code>cvs</code> bucket — downloads use a 10-minute signed URL.
          </p>
        </div>
        <Button asChild variant="secondary">
          <Link to="/careers">
            <ArrowLeft className="h-4 w-4" /> Manage roles
          </Link>
        </Button>
      </header>

      <Card className="p-4 sm:p-5">
        <div className="flex flex-wrap items-end gap-4">
          <Field id="filter-role" label="Role" className="min-w-48">
            <Select
              id="filter-role"
              value={roleFilter}
              onChange={(e) => setParam('role', e.target.value)}
            >
              <option value="all">All roles</option>
              {roles.map((r) => (
                <option key={r.id} value={r.slug}>
                  {r.title}
                </option>
              ))}
            </Select>
          </Field>
          <Field id="filter-status" label="Status" className="min-w-40">
            <Select
              id="filter-status"
              value={statusFilter}
              onChange={(e) => setParam('status', e.target.value)}
            >
              <option value="all">All statuses</option>
              {APPLICATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
          <div className="ml-auto text-xs text-fg-subtle">
            {filtered.length} application{filtered.length === 1 ? '' : 's'}
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-base font-semibold text-fg">No applications yet.</p>
          <p className="mt-2 text-sm text-fg-muted">
            When someone applies via <code>/careers/&lt;slug&gt;/apply</code>, it'll show up here.
          </p>
        </Card>
      ) : (
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(a) => a.id}
          onRowClick={(a) => setViewing(a)}
          empty="No applications match these filters."
        />
      )}

      <EditSheet
        open={viewing !== null}
        onOpenChange={(o) => !o && setViewing(null)}
        title={viewing ? viewing.full_name : 'Application'}
        description={viewing ? viewing.email : undefined}
        size="lg"
      >
        {viewing && (
          <ApplicationDetail
            app={viewing}
            onChangeStatus={changeStatus}
            onRequestDelete={(a) => setDeleting(a)}
            canDelete={canDelete}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this application?"
        description={
          deleting
            ? `"${deleting.full_name}" and their attached CV will be permanently removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete application"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}

function ApplicationDetail({
  app,
  onChangeStatus,
  onRequestDelete,
  canDelete,
}: {
  app: ApplicationRow;
  onChangeStatus: (id: string, status: ApplicationStatus) => void | Promise<void>;
  onRequestDelete: (app: ApplicationRow) => void;
  canDelete: boolean;
}) {
  const [cvHref, setCvHref] = useState<string | null>(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvError, setCvError] = useState<string | null>(null);

  // Reset state when switching rows so the previous CV link doesn't linger.
  useEffect(() => {
    setCvHref(null);
    setCvError(null);
    setCvLoading(false);
  }, [app.id]);

  async function getCv() {
    setCvLoading(true);
    setCvError(null);
    const url = await signedCvUrl(app.cv_path);
    setCvLoading(false);
    if (!url) {
      setCvError('Could not generate download link. Check the cvs bucket policy.');
      return;
    }
    setCvHref(url);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={STATUS_VARIANTS[app.status]}>{STATUS_LABELS[app.status]}</Badge>
        {app.role_slug ? (
          <Badge variant="outline">{app.role_slug}</Badge>
        ) : (
          <Badge>Open application</Badge>
        )}
        <span className="text-xs text-fg-subtle">Received {formatDate(app.created_at)}</span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="detail-email" label="Email">
          <a
            href={`mailto:${app.email}`}
            className="inline-flex items-center gap-1 text-sm text-fg hover:text-gold-500"
          >
            <Mail className="h-3.5 w-3.5" /> {app.email}
          </a>
        </Field>
        <Field id="detail-phone" label="Phone">
          {app.phone ? (
            <a
              href={`tel:${app.phone}`}
              className="inline-flex items-center gap-1 text-sm text-fg hover:text-gold-500"
            >
              <Phone className="h-3.5 w-3.5" /> {app.phone}
            </a>
          ) : (
            <span className="text-sm text-fg-subtle">—</span>
          )}
        </Field>
        <Field id="detail-linkedin" label="LinkedIn">
          {app.linkedin_url ? (
            <a
              href={app.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-fg hover:text-gold-500"
            >
              <Linkedin className="h-3.5 w-3.5" /> Profile
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-sm text-fg-subtle">—</span>
          )}
        </Field>
        <Field id="detail-portfolio" label="Portfolio">
          {app.portfolio_url ? (
            <a
              href={app.portfolio_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-fg hover:text-gold-500"
            >
              <Globe className="h-3.5 w-3.5" /> Open
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-sm text-fg-subtle">—</span>
          )}
        </Field>
      </div>

      <Field id="detail-cover" label="Cover letter">
        {app.cover_letter ? (
          <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-bg-elevated/40 p-3 text-sm text-fg">
            {app.cover_letter}
          </div>
        ) : (
          <span className="text-sm text-fg-subtle">—</span>
        )}
      </Field>

      <Field id="detail-cv" label="CV / Résumé">
        {app.cv_path ? (
          <div className="flex flex-wrap items-center gap-2">
            <FileText className="h-4 w-4 text-gold-500" />
            <code className="text-xs text-fg-muted">{app.cv_path}</code>
            {cvHref ? (
              <Button asChild size="sm" variant="secondary">
                <a href={cvHref} target="_blank" rel="noopener noreferrer">
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={getCv} loading={cvLoading}>
                <Download className="h-3.5 w-3.5" /> Generate download link
              </Button>
            )}
            {cvError && <span className="text-xs text-danger">{cvError}</span>}
          </div>
        ) : (
          <span className="text-sm text-fg-subtle">No CV attached.</span>
        )}
      </Field>

      <Field id="detail-status" label="Update status">
        <Select
          id="detail-status"
          value={app.status}
          onChange={(e) => void onChangeStatus(app.id, e.target.value as ApplicationStatus)}
        >
          {APPLICATION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
      </Field>

      {canDelete && (
        <div className="border-t border-border pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => onRequestDelete(app)}
            className="w-full justify-center text-danger hover:bg-danger/10 hover:text-danger sm:w-auto"
          >
            <Trash2 className="h-4 w-4" /> Delete application
          </Button>
          <p className="mt-2 text-xs text-fg-subtle">
            Permanently removes this application and the attached CV from storage.
          </p>
        </div>
      )}
    </div>
  );
}